import { sumBy } from "lodash";

import { ALL_CHANNELS_KEY } from "@/pages/scenario-builder/v3/api/constants";

import { HistoricalSpendDTO } from "../../../api/getHistoricalSpend";
import { bcpLayerKey } from "../../../utils";
import { Constraint, MediaConstraint } from "../types";
import {
  filterHistoricalSpendsForValidation,
  HistoricalSpends,
  roundDownToNearestThousand,
} from "./logic";
import { SpendConstraints } from "@/pages/scenario-builder/v3/api/v2/getSpendConstraints";

export interface ValidationContext {
  historicalSpends: HistoricalSpends;
  totalBudget: number | null;
}

/**
 * Creates a refine function that validates minSpend <= maxSpend for each constraint
 */
export const minSpendGreaterThanMax = ({
  minValue,
  maxValue,
}: {
  minValue: number;
  maxValue: number;
}) => {
  return minValue > maxValue;
};

/**
 * Creates a refine function that validates maxSpend doesn't exceed historical spend limits
 */
export const maxSpendIsGreaterThanHistoricalSpend = (
  constraint: MediaConstraint,
  historicalSpends: HistoricalSpends
) => {
  const maxSpend = historicalSpends.nodes.get(bcpLayerKey(constraint))?.[constraint.channel]?.max;
  return constraint.maxValue > (maxSpend ?? 0);
};

/**
 * Creates a refine function that validates total constraints don't exceed total budget
 */
export const totalConstraintsExceedTotalBudget = (totalSpend: number, totalBudget: number) => {
  return totalSpend > totalBudget;
};

export const getMaxSpend = (spend: number) => 2 * spend;

export const filterFromSpendConstraints = (
  constraint: Constraint,
  spendConstraintsDTO: SpendConstraints | undefined
) => {
  if (!spendConstraintsDTO) return [];
  return spendConstraintsDTO?.constraints.filter(
    (c) =>
      (!constraint?.brand || c.brand === constraint.brand) &&
      (!constraint?.category || c.category === constraint.category) &&
      (!constraint?.product || c.product === constraint.product) &&
      (!constraint?.channel ||
        c.channel === constraint.channel ||
        constraint.channel === ALL_CHANNELS_KEY) &&
      (!constraint?.geography || c.geography === constraint.geography)
  );
};

export const getAllowedMaxSpendForConstraint = (
  constraint: Constraint,
  historicalSpends: HistoricalSpendDTO[] | undefined,
  spendConstraintsDTO: SpendConstraints | undefined,
  isConstraintsFromApiEnabled: boolean
) => {
  if (isConstraintsFromApiEnabled) {
    const constraints = filterFromSpendConstraints(constraint, spendConstraintsDTO);
    if (constraints?.length === 0 || !constraints) return;
    return sumBy(constraints, "maxSpend") ?? 0;
  }

  if (!historicalSpends) return;

  const isAllChannels = constraint.channel === ALL_CHANNELS_KEY;

  const filteredHistoricalSpends = filterHistoricalSpendsForValidation(historicalSpends, {
    brand: constraint.brand,
    category: constraint.category,
    product: constraint.product,
    channel: constraint.channel,
    geography: constraint.geography,
    isAllChannels,
  });

  if (filteredHistoricalSpends.length === 0) {
    return 0;
  }

  const spendsSum = sumBy(filteredHistoricalSpends, (spend) => spend.marketing_spend.spend);
  const maxSpend = getMaxSpend(spendsSum);

  return roundDownToNearestThousand(maxSpend);
};
